import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  Divider,
  Flex,
  Text,
  Modal,
  ActionIcon,
  TextInput,
  Textarea,
  Switch,
  ThemeIcon,
  Stack,
} from "@mantine/core";
import useAppStore from "./report-recipe-runner-store";
import { shallow } from "zustand/shallow";
import axios from "axios";
import toast from "react-hot-toast";
import { IconPencil, IconCheck, IconTrash, IconX } from "@tabler/icons-react";

import { ShareReportSnapshot } from "@components/ReportRecipe";
import ReportRecipeVisualizer from "./ReportRecipeVisualizer";
import { SnapshotQueryVisualizer } from "@components/ReportRecipe";
import qbSnapshotQueryVisualizerData, {
  retrofitQueryVizData,
  SNAPSHOT_QUERY_VISUALIZER_DATA_VERSION,
} from "./qb-snapshot-query-visualizer-data";
import { UserGroupCreate } from "@components/UserGroup";

export default function SnapshotView({ fetchSnapshots }) {
  const {
    queryData,
    recipeInfo,
    reportData,
    reset,
    snapshot,
    setError,
    setQueryData,
    setReportData,
    setSnapshot,
    savedRecipe,
    setStatBlocks,
    setTableConfig,
  } = useAppStore(
    (state) => ({
      error: state.error,
      queryData: state.query_data,
      recipeInfo: state.recipe_info,
      reportData: state.report_data,
      reset: state.reset,
      savedRecipe: state.saved_recipe,
      snapshot: state.snapshot,
      setError: state.setError,
      setQueryData: state.setQueryData,
      setReportData: state.setReportData,
      setSavedRecipe: state.setSavedRecipe,
      setSnapshot: state.setSnapshot,
      setStatBlocks: state.setStatBlocks,
      setTableConfig: state.setTableConfig,
    }),
    shallow
  );

  const startValue = reportData ? reportData.start_value : null;
  const startDate = startValue ? new Date(startValue) : null;

  useEffect(() => {
    if (snapshot) {
      fetchSnapshotData();
    }
  }, [JSON.stringify(snapshot)]);

  function fetchSnapshotData() {
    if (!snapshot) return;

    const req = {
      visualizers: [],
      report_snapshot_id: snapshot.id,
      snapshot,
    };

    axios
      .post(`/report-snapshots/${snapshot.id}/run-report/`, req)
      .then(({ data }) => {
        const res = data.response[0];
        if (res.response_code) return;
        setQueryData({
          ...queryData,
          visualizer: res.visualizer || queryData.visualizer,
        });
        setReportData(data.response[0].chart_data);
        if (res.meta_block_data?.length) {
          setStatBlocks(res.meta_block_data);
        } else {
          setStatBlocks([]);
        }
        if (res.table_config) {
          setTableConfig(res.table_config);
        }
        // if (
        //   !snapshot.query_visualizer_data ||
        //   JSON.stringify(snapshot.query_visualizer_data) === "{}" ||
        //   snapshot.query_visualizer_data.version !==
        //     SNAPSHOT_QUERY_VISUALIZER_DATA_VERSION
        // ) {
        //   formatQueryVizData(res);
        // }
      })
      .catch((err) => {
        setError(err);
        setReportData(null);
      });
  }

  function formatQueryVizData(oldData) {
    retrofitQueryVizData({
      recipe: recipeInfo,
      query_data: oldData?.chart_data?.query_data,
      snapshot,
    });
  }

  return (
    <div>
      <Box>
        <Stack gap={4}>
          <Flex gap="sm" align="center">
            {/* <IconCamera size={24} /> */}
            <Text fw={600}>{snapshot.title}</Text>
            <SnapshotEdit
              info={snapshot}
              id={snapshot.id}
              onSuccess={() => {
                // fetchSnapshotData();
                fetchSnapshots();
              }}
            />
            <ActionIcon
              size="sm"
              variant="light"
              color="gray"
              title="Clear snapshot"
              radius="xl"
              onClick={() => {
                setSnapshot(null);
                setReportData(null);
                setError(null);
              }}
            >
              <IconX size={12} />
            </ActionIcon>
          </Flex>
          <Text size="sm" maw={600}>
            {snapshot.description || "No description provided"}
          </Text>
          <Flex gap="lg" align="center">
            <Text size="sm">
              snapped {new Date(snapshot.created_at).toLocaleString()}
            </Text>
          </Flex>
          {/* {recipeInfo?.custom_report_comp === "response_filter" && (
            <Box mt="sm">
              <ResponseFilterSnapshotInfo queryData={reportData?.query_data} />
            </Box>
          )} */}
          {snapshot.show_in_bulk_communicator && (
            <Flex gap="xs" align="center">
              <ThemeIcon radius="xl" variant="light" size="xs" color="green">
                <IconCheck size={12} />
              </ThemeIcon>
              <Text size="sm">Available for bulk communication</Text>
            </Flex>
          )}
        </Stack>
        <SnapshotQueryVisualizer data={snapshot.query_visualizer_data} />
      </Box>
      {reportData && (
        <>
          <Divider mt="sm" mb="sm" />
          <ReportRecipeVisualizer />
          <Box mt="xl">
            <UserGroupCreate
              buttonProps={{
                size: "sm",
                variant: "light",
              }}
              reqData={{
                report_recipe_id: recipeInfo.id,
                report_snapshot_id: snapshot.id,
              }}
              formProps={{
                name: "",
                description: `generated from ${
                  snapshot.title
                } (snapshot) ${new Date()
                  .toLocaleString("en-US", { timeZoneName: "short" })
                  .replace(",", "")
                  .replace("at", "")}`,
              }}
            />
          </Box>
        </>
      )}
      {recipeInfo.shareable && (
        <>
          <Divider mt="lg" mb="lg" />
          <ShareReportSnapshot
            reqData={{
              report_snapshot_id: snapshot.id,
            }}
          />
          {/* <Divider mt="lg" mb="lg" /> */}
        </>
      )}
      {/* <DeleteSnapshotButton
        onSuccess={() => {
          setSnapshot(null);
          setReportData(null);
          setError(null);
          fetchSnapshots();
        }}
        snapshotId={snapshot.id}
      /> */}
    </div>
  );
}

const DeleteSnapshotButton = ({ snapshotId, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  function onClick() {
    setLoading(true);

    axios
      .delete(`/report-snapshots/${snapshotId}/`)
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  return (
    <Button
      leftSection={<IconTrash size={16} />}
      color="gray"
      variant="subtle"
      size="xs"
      loading={loading}
      onClick={onClick}
    >
      Delete snapshot
    </Button>
  );
};

const SnapshotEdit = ({ info, id, onSuccess }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ActionIcon
        color="gray"
        onClick={() => setOpen(true)}
        size="sm"
        radius="xl"
        variant="light"
      >
        <IconPencil size={12} />
      </ActionIcon>
      <Modal opened={open} onClose={() => setOpen(false)}>
        <SnapshotForm
          id={id}
          title={info.title}
          description={info.description}
          showInBulkCommunicator={info.show_in_bulk_communicator}
          onSuccess={() => {
            setOpen(false);
            onSuccess();
          }}
        />
      </Modal>
    </>
  );
};

export const SnapshotForm = ({
  id,
  title,
  description,
  showInBulkCommunicator,
  defaultTitle = "",
  onSuccess,
  reqData = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    title: title || "",
    description: description || "",
    show_in_bulk_communicator: showInBulkCommunicator || false,
  });
  const {
    recipeInfo,
    snapshot,
    setSnapshot,
    queryComponents,
    queryData,
    locations,
    orgs,
  } = useAppStore(
    (state) => ({
      queryComponents: state.query_components,
      recipeInfo: state.recipe_info,
      snapshot: state.snapshot,
      setSnapshot: state.setSnapshot,
      queryData: state.query_data,
      locations: state.locations,
      orgs: state.orgs,
    }),
    shallow
  );

  function onSubmit(e) {
    e.preventDefault();
    setLoading(true);

    if (!id) return onCreate();

    return onUpdate();
  }

  function onCreate() {
    const queryVisualizerData = qbSnapshotQueryVisualizerData(
      queryComponents,
      {
        ...queryData,
      },
      recipeInfo,
      {
        locations,
        organizations: orgs,
      }
    );

    const req = {
      ...reqData,
      ...formValues,
      report_snapshot_id: id,
      data_viz: queryVisualizerData,
    };

    if (!req.title && defaultTitle) {
      req.title = defaultTitle;
    }

    axios
      .post(`/report-recipes/save-snapshot/`, req)
      .then(() => {
        setLoading(false);
        onSuccess();
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  function onUpdate() {
    const req = {
      ...formValues,
      report_snapshot_id: id,
    };

    axios
      .put(`/report-snapshots/${id}/`, req)
      .then(() => {
        setLoading(false);
        onSuccess();
        setSnapshot({
          ...snapshot,
          ...formValues,
        });
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  const submitDisabled = !defaultTitle && !formValues.title;

  return (
    <form onSubmit={onSubmit}>
      <TextInput
        label="Title"
        value={formValues.title}
        onChange={(e) =>
          setFormValues({ ...formValues, title: e.target.value })
        }
        mb="sm"
        placeholder={defaultTitle}
      />
      <Textarea
        label="Description"
        value={formValues.description}
        onChange={(e) =>
          setFormValues({ ...formValues, description: e.target.value })
        }
      />
      <Switch
        mt="md"
        mb="md"
        label="Make available for bulk communication"
        checked={formValues.show_in_bulk_communicator}
        onChange={(e) =>
          setFormValues({
            ...formValues,
            show_in_bulk_communicator: e.target.checked,
          })
        }
      />
      <Button
        type="submit"
        mt="sm"
        fullWidth
        loading={loading}
        disabled={submitDisabled}
      >
        Save
      </Button>
    </form>
  );
};

const ResponseFilterSnapshotInfo = ({ queryData }) => {
  if (!queryData || !queryData.saved_selected_questions) return null;
  return (
    <Stack gap="xs">
      {queryData.saved_recipe_orgs &&
        queryData.saved_recipe_orgs.length > 0 && (
          <Text fw={600} size="sm">
            {queryData.saved_recipe_orgs.map((org) => org.name).join(", ")}
          </Text>
        )}
      {queryData.saved_recipe_locations &&
        queryData.saved_recipe_locations.length > 0 && (
          <Text fw={600} size="sm">
            {queryData.saved_recipe_locations
              .map((location) => location.name)
              .join(", ")}
          </Text>
        )}
      {queryData.saved_selected_questions.map((question) => (
        <div key={question.id}>
          <Text fw={600} size="sm">
            {question.title}
          </Text>
          <Text size="sm">
            {question.selected_items.map((item) => item.label).join(", ")}
          </Text>
        </div>
      ))}
    </Stack>
  );
};
